import { memo, useCallback, useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs, { Dayjs } from "dayjs";
import {
  colorActionPrimary,
  colorActionPrimaryText,
  colorBorder,
  colorSurface,
  colorSurfaceHover,
  colorTextDisabled,
  colorTextPrimary,
  colorTextMuted,
  colorDealBg,
  colorDealBorder,
  radiusCard,
  shadowCard,
} from "src/design/tokens";

const WEEK_DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// Stable reference — does not change between renders
const today = dayjs();

interface CalendarProps {
  selectedDate: Dayjs | null;
  onSelectDate: (date: Dayjs) => void;
  availableDates: string[];
  specialDealDates: string[];
}

interface CellProps {
  cell: Dayjs;
  selectedDateStr: string | null;
  availableDates: string[];
  specialDealDates: string[];
  onSelectDate: (date: Dayjs) => void;
}

const CalendarCell = memo(
  ({
    cell,
    selectedDateStr,
    availableDates,
    specialDealDates,
    onSelectDate,
  }: CellProps) => {
    const dateStr = cell.format("YYYY-MM-DD");
    const isPast = cell.isBefore(today, "day");
    const isAvailable = availableDates.includes(dateStr);
    const isDeal = specialDealDates.includes(dateStr);
    const isSelected = selectedDateStr === dateStr;
    const isToday = cell.isSame(today, "day");
    const clickable = isAvailable && !isPast;

    return (
      <Box
        onClick={() => clickable && onSelectDate(cell)}
        sx={{
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          cursor: clickable ? "pointer" : "default",
          backgroundColor: isSelected
            ? colorActionPrimary
            : isDeal && clickable
              ? colorDealBg
              : "transparent",
          color: isSelected
            ? colorActionPrimaryText
            : isPast || !isAvailable
              ? colorTextDisabled
              : colorTextPrimary,
          fontWeight: isToday ? 700 : 400,
          transition: "background-color 0.15s",
          "&:hover":
            clickable && !isSelected
              ? {
                  backgroundColor: isDeal ? colorDealBorder : colorSurfaceHover,
                }
              : {},
        }}
      >
        <Typography
          variant="body2"
          fontSize={13}
          fontWeight="inherit"
          color="inherit"
        >
          {cell.date()}
        </Typography>
      </Box>
    );
  },
);

const CustomCalendar = ({
  selectedDate,
  onSelectDate,
  availableDates,
  specialDealDates,
}: CalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(today.startOf("month"));

  const prevMonth = useCallback(
    () => setCurrentMonth((m) => m.subtract(1, "month")),
    [],
  );
  const nextMonth = useCallback(
    () => setCurrentMonth((m) => m.add(1, "month")),
    [],
  );
  const canGoPrev = currentMonth.isAfter(today.startOf("month"), "month");

  const selectedDateStr = useMemo(
    () => selectedDate?.format("YYYY-MM-DD") ?? null,
    [selectedDate],
  );

  const cells = useMemo<(Dayjs | null)[]>(() => {
    const firstDayOfWeek = currentMonth.startOf("month").day();
    const daysInMonth = currentMonth.daysInMonth();
    return [
      ...Array<null>(firstDayOfWeek).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) =>
        currentMonth.date(i + 1),
      ),
    ];
  }, [currentMonth]);

  return (
    <Box
      sx={{
        backgroundColor: colorSurface,
        border: `1px solid ${colorBorder}`,
        borderRadius: radiusCard,
        boxShadow: shadowCard,
        p: { xs: 1.5, sm: 2 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <IconButton
          size="small"
          onClick={prevMonth}
          disabled={!canGoPrev}
          sx={{ color: colorTextMuted }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="body1" fontWeight={600}>
          {currentMonth.format("MMMM YYYY")}
        </Typography>
        <IconButton
          size="small"
          onClick={nextMonth}
          sx={{ color: colorTextMuted }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          mb: 0.5,
        }}
      >
        {WEEK_DAYS.map((d) => (
          <Typography
            key={d}
            variant="caption"
            textAlign="center"
            color={colorTextMuted}
            fontWeight={500}
            sx={{ py: 0.25 }}
          >
            {d}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.25,
        }}
      >
        {cells.map((cell, idx) =>
          cell ? (
            <CalendarCell
              key={cell.format("YYYY-MM-DD")}
              cell={cell}
              selectedDateStr={selectedDateStr}
              availableDates={availableDates}
              specialDealDates={specialDealDates}
              onSelectDate={onSelectDate}
            />
          ) : (
            <Box key={`e-${idx}`} sx={{ height: 36 }} />
          ),
        )}
      </Box>
    </Box>
  );
};

export default CustomCalendar;
